import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { cn, renderDisplayText } from "@/lib/utils";
import { LoadingLabel, Skel } from "@/components/home/TenantSkeletons";
import { GoogleGlyph } from "@/components/reviews/GoogleGlyph";
import type { ReviewsModel, ServicesModel, TrustItem } from "@/components/home/layouts/models";
import {
  Avatar, HeroActions, HeroCopySkeleton, HeroTitle, Photo, RatingLine, ReviewExtras, ReviewerLine, StarRow,
  type HeroViewProps,
} from "@/components/home/layouts/shared";

/** Soft wave edge in the next section's colour (pass the colour via text-*). */
export function Wave({ className, flip }: { className?: string; flip?: boolean }) {
  return (
    <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className={cn("block h-[44px] w-full md:h-[72px]", flip && "rotate-180", className)} aria-hidden>
      <path fill="currentColor" d="M0 40C180 82 360 82 540 52C720 22 900 10 1080 32C1220 49 1340 60 1440 40V80H0Z" />
    </svg>
  );
}

/** Warm & Welcoming: centred headline over an arched photo collage. */
export function WarmHero({ m, edit }: HeroViewProps) {
  const seq = (n: number) => ({ ["--seq" as string]: n });
  return (
    <section className="v-warm-hero relative -mt-20 overflow-hidden pt-32 md:pt-44" aria-busy={!m.ready}>
      <div className="container relative mx-auto px-5 text-center md:px-6">
        {m.ready ? (
          <div className="mx-auto max-w-4xl">
            <p className="tp-eyebrow tp-seq" style={seq(0)}>{m.eyebrow}</p>
            <HeroTitle m={m} edit={edit} className="tp-display tp-display--md tp-seq mt-6" />
            <div className="tp-lead tp-seq mx-auto mt-6 max-w-2xl" style={seq(2)}>{edit?.subtitle ?? <p>{m.subtitle}</p>}</div>
            <div className="tp-seq mt-9 flex justify-center" style={seq(3)}>
              <HeroActions m={m} edit={edit} className="items-center justify-center" />
            </div>
          </div>
        ) : (
          <HeroCopySkeleton align="center" className="mx-auto max-w-3xl" />
        )}

        <div className="tp-hero-media relative mx-auto mt-14 grid max-w-5xl grid-cols-12 items-end gap-3 sm:gap-5 md:mt-20">
          <div className="v-arch relative col-span-3 aspect-[3/4.4] self-end overflow-hidden bg-muted">
            <Photo src={m.gallery[0]} alt="" loading="lazy" />
          </div>
          <div className="v-arch relative col-span-6 aspect-[4/5] overflow-hidden bg-muted shadow-2xl">
            {m.heroPending ? <Skel className="absolute inset-0 h-full w-full rounded-none" /> : edit?.image ?? <Photo src={m.heroImage} alt="Professional cleaner at work" fetchPriority="high" decoding="async" />}
          </div>
          <div className="v-arch relative col-span-3 aspect-[3/4.4] self-end overflow-hidden bg-muted">
            <Photo src={m.gallery[1]} alt="" loading="lazy" />
          </div>
          <div className="tp-glass tp-hero-float absolute -bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-3 whitespace-nowrap rounded-full px-5 py-3 text-sm">
            {m.reviewsPending ? <Skel className="h-4 w-40 tp-skel-round" /> : (
              <>
                <StarRow className="text-sm" />
                <span className="font-semibold text-foreground">{m.rating.toFixed(1)}</span>
                <span className="hidden text-muted-foreground sm:inline">{m.ratingLabel} · {m.reviewCount > 0 ? `${m.reviewCount} reviews` : "Loved locally"}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <Wave className="mt-10 text-background md:mt-16" />
    </section>
  );
}

export function WarmTrust({ items }: { items: TrustItem[] }) {
  return (
    <section aria-label="Why customers trust us" className="pb-4 pt-2">
      <div className="container mx-auto px-5 md:px-6">
        <ul className="flex flex-wrap items-center justify-center gap-3">
          {items.map(({ key, Icon, title, subtitle }, i) => (
            <li key={key} className="flex items-center gap-3 rounded-full bg-secondary px-5 py-3 text-sm" data-reveal style={{ ["--reveal-delay" as string]: `${i * 70}ms` }}>
              <Icon className="h-4 w-4 text-primary" strokeWidth={1.75} />
              <span><span className="font-semibold text-foreground">{title}</span><span className="text-muted-foreground"> · {subtitle}</span></span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function WarmServices({ s }: { s: ServicesModel }) {
  return (
    <section className="tp-section">
      <div className="container mx-auto px-5 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="tp-eyebrow" data-reveal>What we do</p>
          <h2 className="tp-h2 mt-5" data-reveal>{renderDisplayText(s.title)}</h2>
          <p className="tp-lead mt-5" data-reveal>{renderDisplayText(s.lead)}</p>
        </div>
        {s.loading ? (
          <div className="tp-skeleton mt-14 grid gap-6 md:grid-cols-2" aria-busy="true">
            <LoadingLabel>Loading services</LoadingLabel>
            {[0, 1].map((i) => <Skel key={i} className="aspect-[4/3] w-full rounded-[2rem]" />)}
          </div>
        ) : (
          <div className={cn("mt-14 grid gap-6 md:mt-16 md:gap-8", s.items.length === 1 ? "mx-auto max-w-xl" : "md:grid-cols-2")}>
            {s.items.map((item, i) => (
              <Link key={item.key} to={s.servicesHref} className="group block" data-reveal style={{ ["--reveal-delay" as string]: `${(i % 2) * 100}ms` }}>
                <div className="v-warm-photo relative aspect-[4/3] overflow-hidden bg-muted">
                  <Photo src={item.image} alt={item.category} className="tp-zoom" loading="lazy" />
                  <span className="tp-glass absolute bottom-4 left-4 rounded-full px-4 py-2 text-sm font-semibold text-foreground">{item.price}</span>
                </div>
                <div className="px-3 pt-6 text-center md:text-left">
                  <h3 className="tp-h3">{renderDisplayText(item.title)}</h3>
                  {item.description && <p className="mt-2 text-muted-foreground">{item.description}</p>}
                  <span className="tp-link mt-3 text-sm">Learn more <ArrowRight className="h-4 w-4" /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
        <div className="mt-12 text-center"><Link to={s.servicesHref} className="tp-link text-sm">View all services <ArrowRight className="h-4 w-4" /></Link></div>
      </div>
    </section>
  );
}

export function WarmReviews({ r, reviewFormHref }: { r: ReviewsModel; reviewFormHref?: string }) {
  const [feature, ...rest] = r.items;
  return (
    <section className="tp-section v-warm-reviews relative overflow-hidden bg-secondary/70" aria-busy={r.pending}>
      <div className="container relative mx-auto px-5 md:px-6">
        <p className="tp-eyebrow mx-auto flex justify-center" data-reveal>Kind words</p>
        {r.pending ? (
          <div className="tp-skeleton mx-auto mt-8 max-w-3xl space-y-4" aria-hidden><LoadingLabel>Loading reviews</LoadingLabel><Skel className="h-8 w-full tp-skel-round" /><Skel className="h-8 w-4/5 tp-skel-round" /></div>
        ) : (
          <>
            {feature && (
              <figure className="mx-auto mt-8 max-w-3xl text-center" data-reveal data-testid={feature.google ? "google-review-card" : undefined}>
                <Quote className="mx-auto h-12 w-12 text-primary/30" strokeWidth={1.25} aria-hidden />
                <blockquote className="mt-4">
                  <p className="font-display text-[clamp(1.5rem,1.1rem+1.6vw,2.4rem)] font-medium leading-snug text-foreground">{feature.quote}</p>
                </blockquote>
                <figcaption className="mt-8 flex flex-col items-center gap-3">
                  <Avatar item={feature} className="h-14 w-14" />
                  <div><ReviewerLine item={feature} nameClassName="text-base" /></div>
                  <StarRow n={feature.rating} className="text-base" />
                </figcaption>
              </figure>
            )}
            {rest.length > 0 && (
              <div className="mt-14 grid gap-5 md:grid-cols-2 lg:mt-16">
                {rest.map((item) => (
                  <figure key={item.key} className="rounded-[2rem] bg-card p-7 shadow-sm md:p-9" data-reveal data-testid={item.google ? "google-review-card" : undefined}>
                    <div className="flex items-center justify-between"><StarRow n={item.rating} className="text-sm" />{item.google && <GoogleGlyph className="h-5 w-5" />}</div>
                    <blockquote className="mt-4"><p className="line-clamp-5 text-lg leading-relaxed text-foreground">{item.quote}</p></blockquote>
                    <figcaption className="mt-6 flex items-center gap-3"><Avatar item={item} className="h-10 w-10" /><div className="min-w-0"><ReviewerLine item={item} /></div></figcaption>
                  </figure>
                ))}
              </div>
            )}
            <div className="mt-10 flex flex-col items-center gap-4 text-center">
              <p className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">{r.label}</span> · {r.average.toFixed(1)} · {r.countLine}</p>
              <ReviewExtras google={r.google} reviewFormHref={reviewFormHref} className="justify-center" />
            </div>
          </>
        )}
      </div>
    </section>
  );
}
